import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Tooltip, 
  Snackbar, 
  IconButton, 
  Chip,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Styled components
const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 0),
  backgroundColor: theme.palette.grey[50],
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}));

const TypographyExample = styled(Box)(({ variant, fontFamily, fontWeight, fontSize, lineHeight }) => ({
  fontFamily: fontFamily || 'inherit',
  fontWeight: fontWeight || 'inherit',
  fontSize: fontSize || 'inherit',
  lineHeight: lineHeight || 'inherit',
  margin: '8px 0',
  padding: '8px',
  borderLeft: '4px solid',
  borderColor: 'primary.main',
  backgroundColor: 'background.paper',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const ColorSwatch = styled(Box)(({ color }) => ({
  width: '100%',
  height: '80px',
  borderRadius: '4px',
  marginBottom: '8px',
  border: '1px solid rgba(0,0,0,0.1)',
  backgroundColor: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontWeight: 500,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    margin: '0 auto',
  },
}));

const BrandGuidelines = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Brand colors - Matches the theme configuration in shared/theme/theme.config.js
  const brandColors = [
    { name: 'Primary', value: '#005F94', usage: 'Primary brand color, main buttons, important elements' },
    { name: 'Secondary', value: '#006D87', usage: 'Secondary actions, accents' },
    { name: 'Error', value: '#D15B5B', usage: 'Error messages, destructive actions' },
    { name: 'Warning', value: '#D19C5B', usage: 'Warning messages, attention needed' },
    { name: 'Info', value: '#5B9CD1', usage: 'Informational messages' },
    { name: 'Success', value: '#5BB8A7', usage: 'Success messages, positive actions' },
    { name: 'Background', value: '#F6F8FA', usage: 'Page background' },
    { name: 'Paper', value: '#FFFFFF', usage: 'Cards, panels, surfaces' },
    { name: 'Text Primary', value: '#1A364D', usage: 'Main text, headings' },
    { name: 'Text Secondary', value: '#2C4B63', usage: 'Secondary text, captions' },
    { name: 'Text Disabled', value: '#8FA0AD', usage: 'Disabled text, placeholders' }
  ];

  // Typography scale - Matches the theme configuration
  const typographyScale = [
    { 
      name: 'H1', 
      variant: 'h1',
      fontWeight: 700, 
      fontSize: '2.5rem', 
      lineHeight: 1.2, 
      example: 'Page Title',
      usage: 'Main page headings' 
    },
    { 
      name: 'H2', 
      variant: 'h2',
      fontWeight: 600, 
      fontSize: '2rem', 
      lineHeight: 1.3, 
      example: 'Section Heading',
      usage: 'Section headings' 
    },
    { 
      name: 'H3', 
      variant: 'h3',
      fontWeight: 600, 
      fontSize: '1.75rem', 
      lineHeight: 1.3, 
      example: 'Subsection',
      usage: 'Subsection headings' 
    },
    { 
      name: 'H4', 
      variant: 'h4',
      fontWeight: 600, 
      fontSize: '1.5rem', 
      lineHeight: 1.4, 
      example: 'Card Title',
      usage: 'Card titles' 
    },
    { 
      name: 'Body 1', 
      variant: 'body1',
      fontWeight: 400, 
      fontSize: '1rem', 
      lineHeight: 1.5, 
      example: 'This is the main body text used for paragraphs and general content.',
      usage: 'Main body text' 
    },
    { 
      name: 'Body 2', 
      variant: 'body2',
      fontWeight: 400, 
      fontSize: '0.875rem', 
      lineHeight: 1.5, 
      example: 'This is secondary text used for captions and less prominent content.',
      usage: 'Secondary text, captions' 
    },
    { 
      name: 'Caption', 
      variant: 'caption',
      fontWeight: 400, 
      fontSize: '0.75rem', 
      lineHeight: 1.66, 
      example: 'This is caption text',
      usage: 'Captions, helper text' 
    },
  ];

  // Font families - matches actual theme configuration
  const fontFamilies = [
    {
      name: 'Open Sans',
      value: 'Open Sans',
      example: 'Dashboard AI - Humanitarian Data Analysis & Insights',
      usage: 'All text content including headings, body text, buttons, and UI elements'
    }
  ];
  
  // State for snackbar
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  
  // Branding snippet for AI prompts
  const brandingSnippet = [
    '# Dashboard AI Brand Guidelines',
    '',
    '## Colors',
    '- Primary: #005F94 (main brand color, primary actions, important elements)',
    '- Secondary: #006D87 (secondary actions, accents)',
    '- Error: #D15B5B (error messages, destructive actions)',
    '- Warning: #D19C5B (warning messages, attention needed)',
    '- Info: #5B9CD1 (informational messages)',
    '- Success: #5BB8A7 (success messages, positive actions)',
    '- Background: #F6F8FA (page background)',
    '- Paper: #FFFFFF (cards, panels, surfaces)',
    '- Text Primary: #1A364D (main text, headings)',
    '- Text Secondary: #2C4B63 (secondary text, captions)',
    '- Text Disabled: #8FA0AD (disabled text, placeholders)',
    '',
    '## Typography',
    "- Primary Font: Open Sans",
    '- Base font size: 16px',
    '- Line height: 1.5 for body, 1.2-1.4 for headings',
    '- Font weights: 400 (regular), 500 (medium), 600 (semibold), 700 (bold)',
    '',
    '## Logo & Brand Identity Usage',
    '- Primary branding combines logo with situation-room.ai URL',
    '- Use logo + URL combination on headers and main branding areas',
    '- Standalone logo acceptable for smaller spaces or secondary usage',
    "- White variant available for dark backgrounds",
    "- Maintain minimum clear space around the logo",
    "- Never stretch or distort the logo",
    '- situation-room.ai should use Open Sans font, bold weight',
    '- Logo file: https://situation-room.ai/favico.svg',
    '',
    '## Usage Guidelines',
    '- Use primary blue (#005F94) for primary actions and important elements',
    '- Maintain sufficient contrast for accessibility (4.5:1 minimum for normal text)',
    '- Use consistent spacing (8px grid system)',
    '- Ensure interactive elements have clear visual feedback',
    '- Follow WCAG 2.1 AA accessibility standards'
  ].join('\n');

  // Copy to clipboard function
  const copyToClipboard = () => {
    navigator.clipboard.writeText(brandingSnippet)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  return (
    <Box sx={{ pt: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h1" component="h1" gutterBottom>
          Brand Guidelines
        </Typography>
        <Typography variant="body1" paragraph>
          This document outlines the visual and design standards for the Dashboard AI application.
          Consistent use of these guidelines helps maintain a cohesive and professional appearance.
        </Typography>

        {/* Logo Section */}
        <Section>
          <Typography variant="h2" gutterBottom>Logo & Brand Identity</Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: '800px' }}>
            The Dashboard AI brand identity consists of our logo and the situation-room.ai domain. 
            These elements work together to create a cohesive and professional brand presence.
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Primary Brand Lockup</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The preferred brand presentation combines our logo with the situation-room.ai domain
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem', // --spacing-md
                    p: '1rem', // --spacing-md
                    backgroundColor: '#005F94',
                    borderRadius: 1,
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <img 
                      src="/favico.svg" 
                      alt="Dashboard AI Logo" 
                      style={{ 
                        width: '32px',
                        height: '32px',
                        filter: 'brightness(0) invert(1)',
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ 
                      color: 'white',
                      fontFamily: 'Open Sans, sans-serif',
                      fontWeight: 700, // --font-weight-bold
                      fontSize: '1.140205rem', // --font-size-xl (20px)
                      letterSpacing: '0.02em',
                      lineHeight: 1.25 // --line-height-tight
                    }}>
                      situation-room.ai
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Use this lockup for headers, business cards, and primary brand applications
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => {
                        navigator.clipboard.writeText('https://situation-room.ai/favico.svg');
                        setSnackbarOpen(true);
                      }}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Copy Logo URL
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Primary Logo</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    p: 4,
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <img 
                      src="/favico.svg" 
                      alt="Dashboard AI Logo" 
                      style={{ height: '80px' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Standalone logo for secondary applications (use actual logo file)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Reverse Logo</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    p: 4,
                    backgroundColor: '#005F94',
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <img 
                      src="/favico.svg" 
                      alt="Dashboard AI Logo" 
                      style={{ 
                        height: '80px',
                        filter: 'brightness(0) invert(1)'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    White text logo for dark backgrounds (use white version of actual logo file)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Clear Space</Typography>
            <Typography variant="body2" paragraph>
              Maintain a minimum clear space around the logo equal to the height of the 'D' in the logo.
              This ensures the logo has proper breathing room and visibility.
            </Typography>
          </Box>
        </Section>

        {/* Color Palette */}
        <Section>
          <Typography variant="h2" gutterBottom>Color Palette</Typography>
          <Typography variant="body1" paragraph>
            The Dashboard AI color palette has been carefully selected to ensure accessibility,
            readability, and visual harmony. Always use these exact color values to maintain consistency.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {brandColors.map((color) => (
              <Grid item xs={6} sm={4} md={3} key={color.name}>
                <ColorSwatch color={color.value} />
                <Typography variant="subtitle2">{color.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    {color.value}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      navigator.clipboard.writeText(color.value);
                      setSnackbarOpen(true);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                  </IconButton>
                </Box>
                <Typography variant="body2">
                  {color.usage}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Section>


        {/* Typography */}
        <Section>
          <Typography variant="h2" gutterBottom>Typography</Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: '800px' }}>
            Our typography system is built on a harmonious scale that creates clear visual hierarchy and ensures
            excellent readability across all devices. We use Open Sans as our primary typeface for its clean,
            modern appearance and excellent legibility at various sizes.
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            {/* Font Family */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                Font Family
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" sx={{ 
                  fontFamily: 'Open Sans, sans-serif',
                  fontWeight: 700,
                  color: 'text.primary'
                }}>
                  Open Sans
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    navigator.clipboard.writeText('Open Sans');
                    setSnackbarOpen(true);
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Used for all text content including headings, body text, buttons, and UI elements
              </Typography>
              
            </Box>

            {/* Typography Scale */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                Typography Scale
              </Typography>
              <Grid container spacing={3}>
                {typographyScale.map((type) => (
                  <Grid item xs={12} sm={6} md={4} key={type.name}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {type.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {type.fontSize} • {type.fontWeight} • Line: {type.lineHeight}
                      </Typography>
                      <Typography 
                        variant={type.variant}
                        sx={{
                          fontFamily: 'Open Sans, sans-serif',
                          fontWeight: type.fontWeight,
                          fontSize: type.fontSize,
                          lineHeight: type.lineHeight,
                          color: 'text.primary'
                        }}
                      >
                        {type.example}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

          </Box>
        </Section>
        
        {/* Branding Snippet */}
        <Section>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h2" gutterBottom>AI Branding Snippet</Typography>
            <Tooltip title="Copy to clipboard">
              <Button 
                variant="outlined" 
                startIcon={<ContentCopyIcon />}
                onClick={copyToClipboard}
                size="small"
              >
                Copy
              </Button>
            </Tooltip>
          </Box>
          <Typography variant="body1" paragraph>
            Use this snippet when working with AI tools like image generators or content creators to ensure
            brand consistency. It includes all the essential brand guidelines in a format that works well with AI prompts.
          </Typography>
          <Box 
            component="pre" 
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              position: 'relative',
              fontFamily: '\'Roboto Mono\', monospace',
              fontSize: '0.875rem',
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {brandingSnippet}
          </Box>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message="Branding snippet copied to clipboard!"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setSnackbarOpen(false)}
              >
                ✕
              </IconButton>
            }
          />
        </Section>

        {/* Buttons & Interactive Elements */}
        <Section>
          <Typography variant="h2" gutterBottom>Buttons & Interactive Elements</Typography>
          <Typography variant="body1" paragraph>
            Interactive elements should be clearly distinguishable and provide appropriate feedback
            on interaction.
          </Typography>
          
          <Box sx={{ mt: 4, '& > *': { m: 1 } }}>
            <Typography variant="h6" gutterBottom>Button Styles</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Primary</Typography>
                <Box sx={{ 
                  display: 'inline-block',
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 1,
                  fontWeight: 500,
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'primary.dark' }
                }}>
                  Primary Button
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Secondary</Typography>
                <Box sx={{ 
                  display: 'inline-block',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  px: 3,
                  py: 1.5,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}>
                  Secondary Button
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Text Button</Typography>
                <Box sx={{ 
                  display: 'inline-block',
                  color: 'primary.main',
                  px: 2,
                  py: 1.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}>
                  Text Button
                </Box>
              </Box>
            </Box>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Form Elements</Typography>
            <Box sx={{ maxWidth: 400, '& > *': { mb: 2 } }}>
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Text Field</Typography>
                <Box sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: '8px 12px',
                  '&:hover': { borderColor: 'text.primary' },
                  '&:focus-within': { 
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 1px primary.main'
                  }
                }}>
                  <input 
                    type="text" 
                    placeholder="Placeholder text"
                    style={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      backgroundColor: 'transparent',
                      fontFamily: 'var(--font-family-body, "Roboto", sans-serif)',
                      fontSize: '1rem'
                    }}
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Dropdown</Typography>
                <Box sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: '8px 12px',
                  position: 'relative',
                  '&:after': {
                    content: '"⌄"',
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }
                }}>
                  <select 
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      appearance: 'none',
                      fontFamily: 'var(--font-family-body, "Roboto", sans-serif)',
                      fontSize: '1rem',
                      color: theme.palette.text.secondary
                    }}
                  >
                    <option>Select an option</option>
                    <option>Option 1</option>
                    <option>Option 2</option>
                  </select>
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" display="block" gutterBottom>Checkbox</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 20,
                    height: 20,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      bgcolor: 'primary.main',
                      borderRadius: 0.5
                    }} />
                  </Box>
                  <Typography variant="body2">Checkbox option</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Section>


        
        <Box sx={{ mt: 6, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default BrandGuidelines;
